// N4-3 — THE ASK LALA HANDOFF, shared logic.
//
// Lives in its own module (not inside App.tsx) for the same reason src/zodiac.js and src/nameParts.js do:
// the fast gate has to be able to assert what Lala would actually RECEIVE, and a gate cannot import TSX.
// One definition, imported by the app and by the gate, so what we ship and what we check cannot drift.

// WHICH SURFACES GET THE BUTTON, decided by construction rather than by remembering where to paste it.
// This is exactly the owner's list of READINGS. Everything else renders nothing: the Seal Creator above
// all, but also the Magic Room's made objects (spells, petitions, oils, keys and Moses workings, the
// Cauldron), because those are things a member MAKES, not readings to be discussed. So the button can be
// placed in every action row uniformly and still cannot appear where she ruled it must not.
export const ASK_LALA_READING_TYPES = new Set([
  "tarot", "tarot-spread",                                          // every tarot reading + the daily card
  "dream",                                                          // dream interpretation
  "astrocartography-location", "astrocartography-map", "astrocartography-relocation",
  "human-design", "human-design-compat",
  "birth-chart", "astrology-report", "astrology-tradition", "compatibility", "relationship",
  "numerology", "personality", "find-your-type",
  "mini-report", "prediction", "question-reading", "bubble-reading", // every mini-reading + daily guidance
  "palm-reading",
  // L11: the reading a stranger was given before she was a member, which lands on her shelf on
  // conversion. **IT IS ON THIS ROSTER FOR R184's SECOND REASON RATHER THAN FOR THE BUTTON:** an
  // unrostered type makes a delivered reading's own boilerplate read as THE MEMBER'S OWN SPEECH, and
  // every conduct classifier then runs on words this house wrote — the exact incident N17-2 exists
  // for. It is a real chart reading and belongs beside every other one.
  "funnel-reading",
  // AKASHIC-1 (her order, 2026-09-01). Every Akashic reading ends with her own line, "Go deeper
  // into your records with Lala", and the report rides into Lala's context from it — so this roster
  // is not a nicety here, it is that closing sentence made real. R25 refused the push while the five
  // were absent, which is exactly what that gate is for: a delivered reading with no door is a
  // member who paid for it and cannot discuss it, with nothing thrown and nothing logged.
  "akashic-soul-record", "akashic-past-life-connections", "akashic-soul-contracts",
  "akashic-past-life-gifts", "akashic-purpose-timeline",
]);

// R184 — **THIS ROSTER HAS TWO JOBS AND ONLY ONE OF THEM IS WRITTEN ABOVE.** The paragraph above is
// R25's: which delivered readings get the Ask Lala door, which is a decision about a BUTTON. The
// second job is N17-2's and it is not a UI decision at all: `memberSpokenText` refuses to treat a
// message as a handoff unless the declared type is on THIS list, so a type that is not on it makes a
// delivered reading's own boilerplate read as **the member's own speech** and every conduct
// classifier runs on it — which is the exact incident N17-2 was built for ("your relationship with
// family" answered with "Watch how you come at my children."). **Measured, not reasoned:** the same
// message under a rostered type hands back the opener alone and under an unrostered one hands back
// the whole reading. So adding or removing a row here for a BUTTON reason silently moves what may be
// excused from the conduct gate, in both directions, and that coupling was written in neither place.
//
// **AND THE MEMBERSHIP QUESTION WAS ASKED FOUR TIMES IN SHIPPED CODE** (R28/R29): here, in
// `handoffKindFor`, in `memberSpokenText`, and in `AskLalaAboutButton` — and the one that was a
// NAMED function was the one nobody called (R38's trap, R180's pairing: an uncalled export and the
// hand-written expression that replaced it are one fact seen from two ends). There is ONE `.has` on
// this roster now and everything else reaches it.
//
// TWO EXPORTS RATHER THAN A FLAG (R21/R107b/R150/R158): two callers hold a TYPE and two hold the
// delivered thing, and making a call site remember which shape to unwrap is how the fourth spelling
// arrives.
export function isAskLalaReadingType(type) {
  return ASK_LALA_READING_TYPES.has(String(type || ""));
}

export function isAskLalaReading(report) {
  return isAskLalaReadingType(report?.type);
}

// N16-1 — WHICH KIND OF READING ARRIVED. An astrology-surface handoff is ABOUT the chart, so full
// chart use is the subject; every other reading is about the cards, the dream, the numbers, and her
// chart tools are not the frame. One definition, used by the prompt and by the gate.
//
// R30 — THE SPLIT IS A RULE ABOUT ROOMS, AND IT IS WRITTEN DOWN HERE RATHER THAN LEFT AS THE SHAPE OF
// A HAND-TYPED LIST. Three rooms read the chart itself; the rest read cards, dreams, numbers, hands.
// The membership below is exactly "its room is one of these", and `scripts/r30-handoff-kind.mjs`
// holds that biconditional against the SERVER's own `ROOM_FOR_TYPE`, so an astrology reading added
// tomorrow joins by EXISTING rather than by somebody remembering this file.
//
// It was not a rule before, and two readings fell through: `personality` and `find-your-type` are
// both Astrology-room readings written FROM the placements ("Key placements: ..."; "drawn entirely
// from their own chart"), and both handed off as kind "reading" — which tells Lala, in the directive
// below and through `handoffFramingViolations` which ENFORCES it, that their chart is not the frame
// and their placements are not to be recited. The gate then strips the paragraphs that answer the
// question she asked about the reading she just paid for.
export const CHART_HANDOFF_ROOMS = new Set(["astrology", "astrocartography", "human-design"]);
export const ASK_LALA_ASTROLOGY_TYPES = new Set([
  "astrocartography-location", "astrocartography-map", "astrocartography-relocation",
  "human-design", "human-design-compat",
  "birth-chart", "astrology-report", "astrology-tradition", "compatibility", "relationship",
  // Every mini-reading in the Astrology room is built on her house maps, so they are chart surfaces.
  "mini-report", "prediction", "question-reading", "bubble-reading",
  // R30: the two the rule was already true of and the list did not say.
  "personality", "find-your-type",
  // L11: cast from her own birth data through the house's own engines (her F3 clause is REAL
  // ASTROLOGY UNDERNEATH), so it is a chart surface and Lala is told so.
  "funnel-reading",
]);

export function handoffKindFor(type) {
  const t = String(type || "");
  if (!isAskLalaReadingType(t)) return "";
  return ASK_LALA_ASTROLOGY_TYPES.has(t) ? "astrology" : "reading";
}

// N17-2 — THE HANDOFF ENVELOPE. A handed-off reading is NOT the member speaking.
//
// The incident this exists for: an astrocartography map's own boilerplate ("your relationship with
// family", "low-drama") arrived as the member's message and the family gate answered a delivered
// reading with "Watch how you come at my children." The reading text was never hers; it was ours.
//
// So the message carries a STRUCTURE the server can read: a fixed opener the member is treated as
// having said, and a body that is quoted reading content. `memberSpokenText` returns only the part
// the member actually spoke, and every conduct classifier runs on THAT. The full text still reaches
// Lala and still drives retrieval, because she must read the whole reading to answer it.
const HANDOFF_OPENER_RE = /^Here is my [\s\S]{1,200}?Read it with me and tell me what you see in it\./;

// T5-2 — THE MEMBER'S OWN WORDS TRAVEL WITH THE READING, AND MUST STAY HERS.
//
// With the intercept box she now sends SELECTED QUESTIONS and her own typed line alongside the
// reading. Both are her speech and must be classified as her speech, so the envelope needs a visible
// boundary rather than a fixed one-sentence opener: everything BEFORE the mark is the member talking,
// everything after it is quoted reading content. The old opener-only form is still parsed, so a
// message composed by an older client (or a handoff sent with no questions at all) behaves exactly
// as it did.
export const HANDOFF_READING_MARK = "Here is the reading, in full:";
export const HANDOFF_QUESTIONS_LEAD = "What I want to know:";

export function splitHandoff(message) {
  const s = String(message || "");
  const at = s.indexOf(HANDOFF_READING_MARK);
  if (at > -1 && HANDOFF_OPENER_RE.test(s)) {
    return { isHandoff: true, ask: s.slice(0, at).trim(), body: s.slice(at + HANDOFF_READING_MARK.length).trim() };
  }
  const m = HANDOFF_OPENER_RE.exec(s);
  if (!m) return { isHandoff: false, ask: s, body: "" };
  return { isHandoff: true, ask: m[0].trim(), body: s.slice(m[0].length).trim() };
}

// The suggested questions a reading was born with (T5-1). A reading that predates the feature has
// none, and none are ever made for it after the fact.
export function suggestedQuestionsOf(report) {
  const list = report && Array.isArray(report.suggestedQuestions) ? report.suggestedQuestions : [];
  return list.filter((q) => typeof q === "string" && q.trim()).map((q) => q.trim());
}

// THE ONE COMPOSER for a handoff turn. Selected questions and anything she typed go in front of the
// reading, as her words, and the whole thing is ONE message: one turn, one generation, one answer.
// With nothing selected and nothing typed it returns EXACTLY the old payload, byte for byte.
export function composeHandoffMessage({ report, kind, subject, questions = [], typed = "" }) {
  const reading = readingToAskText(report, kind, subject);
  if (!reading) return "";
  const picked = (Array.isArray(questions) ? questions : []).map((q) => String(q || "").trim()).filter(Boolean);
  const own = String(typed || "").trim();
  if (!picked.length && !own) return reading;

  // The opener is the first line of the flattened reading; her words follow it, then the mark, then
  // the reading itself repeated in full. Splitting on the first blank line keeps ONE definition of
  // the opener (readingToAskText's), so the envelope regex can never drift from what we send.
  const [opener, ...rest] = reading.split("\n\n");
  const parts = [opener];
  if (picked.length) parts.push([HANDOFF_QUESTIONS_LEAD, ...picked.map((q) => `- ${q}`)].join("\n"));
  // SACRED WORDS: what she typed travels VERBATIM, never cleaned up, never summarised.
  if (own) parts.push(`In my own words: ${own}`);
  parts.push(HANDOFF_READING_MARK);
  parts.push(rest.join("\n\n"));
  return parts.join("\n\n").trim();
}

// The member's OWN words in this turn. Both conditions must hold before any text is treated as
// non-member content: the client declared a handoff of a known reading type, AND the message really
// carries the canonical envelope. A typed message can never satisfy the first, so nothing a member
// types is ever excused, and a hostile typed ask still trips L3 exactly as it does today.
export function memberSpokenText(message, handoff) {
  const s = String(message || "");
  // R184 — the same predicate the button asks, reached rather than re-typed. This is the reader that
  // makes the roster a CONDUCT boundary as well as a UI one: an unrostered type returns the message
  // WHOLE, quoted reading content and all, to every classifier below.
  if (!handoff || !isAskLalaReading(handoff)) return s;
  const split = splitHandoff(s);
  return split.isHandoff ? split.ask : s;
}

// Flatten a delivered reading into the words Lala receives. Deliberately plain and COMPLETE: she is
// evaluating and deepening what the member is holding, so she needs the actual content, not a summary.
// Capped so an enormous report cannot blow the context window; the cap is far above any real reading.
export function readingToAskText(report, kind, subject) {
  const r = report || {};
  const parts = [];
  const label = String(r.kicker || r.title || kind || "my reading").trim();
  const who = subject && String(subject).trim() ? ` It is for ${String(subject).trim()}.` : "";
  parts.push(`Here is my ${label}.${who} Read it with me and tell me what you see in it.`);
  if (r.question) parts.push(`What I asked: ${String(r.question)}`);
  if (r.spreadLabel) parts.push(`Spread: ${String(r.spreadLabel)}`);
  // Tarot-shaped readings carry their cards; every card, its position and its orientation matter.
  for (const c of Array.isArray(r.cards) ? r.cards : []) {
    const nm = String(c?.name || "").trim();
    if (!nm) continue;
    const pos = c.position ? `${String(c.position)}: ` : "";
    const rev = c.orientation === "reversed" ? " (reversed)" : "";
    const interp = c.interpretation ? `. ${String(c.interpretation)}` : "";
    parts.push(`${pos}${nm}${rev}${interp}`);
  }
  if (r.overview) parts.push(String(r.overview));
  for (const s of Array.isArray(r.sections) ? r.sections : []) {
    if (!s?.body) continue;
    parts.push(`${s.heading ? `${String(s.heading)}: ` : ""}${String(s.body)}`);
  }
  if (r.evaluation) parts.push(String(r.evaluation));
  return parts.filter(Boolean).join("\n\n").slice(0, 12000);
}
